import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import '../../components/Tables/Admin/AdminTable.css';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface Tenant {
    id: number;
    name: string;
    plan: string;
    created_at: string;
    users_count?: number;
}

const TenantsScreen: React.FC = () => {
    const { token } = useAuth();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchTenants = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/tenants`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setTenants(data);
            } else {
                console.error('Failed to fetch tenants');
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchTenants();
        }
    }, [token, fetchTenants]);

    const filteredTenants = tenants.filter(tenant =>
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <SiteLayout>
            <Header icon='business' title='Gestion des Tenants' />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b' }}>Organisations clientes</h1>
                    <p style={{ color: '#64748b' }}>Manage organizations and subscription plans.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        placeholder="Search tenants..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-input"
                        style={{ width: '250px' }}
                    />
                    <button className="btn btn-primary" onClick={() => window.location.href = '/admin/tenants/new'}>
                        + Add Tenant
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading tenants...</div>
            ) : (
                <div className="admin-table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Plan</th>
                                <th>Users</th>
                                <th>Created At</th>
                                <th>Status</th>
                                <th className="right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTenants.map(tenant => (
                                <tr key={tenant.id}>
                                    <td>
                                        <div style={{ fontWeight: '500' }}>{tenant.name}</div>
                                    </td>
                                    <td>
                                        <span className={`badge ${tenant.plan === 'Enterprise' ? 'badge-info' : tenant.plan === 'Pro' ? 'badge-success' : 'badge-warning'}`}>
                                            {tenant.plan}
                                        </span>
                                    </td>
                                    <td>{tenant.users_count} Users</td>
                                    <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <span className="badge badge-success">Active</span>
                                    </td>
                                    <td className="right">
                                        <button className="action-btn" title="Edit Tenant">
                                            <i className="material-icons">edit</i>
                                        </button>
                                        <button className="action-btn" title="View Details">
                                            <i className="material-icons">visibility</i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SiteLayout>
    );
};

export default TenantsScreen;
